import { Fragment } from 'react';

import { dayBars, lineBars } from './bamsShortsCharts';
import { categorise, CATS, fmt, gridShade, pct, RAMP } from './bamsShortsUtils';

// Day & line tab: per-site daily bars, an expandable daily performance table, the
// item-by-day grid, and the by-pack-line breakdown. All models are derived upstream in
// `dayModel`; `dayDetailItems` resolves the items behind an expanded day row.
export function DayLineView({
    dark,
    dayBusy,
    dayDetailItems,
    dayErr,
    dayModel,
    days,
    openDay,
    setDaySite,
    setOpenDay,
}) {
    return (
        <>
            {dayBusy ? (
                <div className="status">
                    <span className="spin" />
                    Loading day &amp; line detail…
                </div>
            ) : null}
            {dayErr ? (
                <div className="errbox">
                    <b>Could not load day &amp; line detail.</b> {dayErr}
                </div>
            ) : null}
            {!dayBusy && !dayErr && dayModel ? (
                <>
                    <div className="panel">
                        <div className="bar" style={{ marginBottom: 8 }}>
                            <label className="inline" htmlFor="bams-site">
                                Site
                            </label>
                            <select
                                id="bams-site"
                                style={{ minWidth: 220 }}
                                value={dayModel.site}
                                onChange={(e) => {
                                    setDaySite(e.target.value);
                                    setOpenDay(null);
                                }}
                            >
                                {dayModel.keys.map((k) => {
                                    const [b, f] = k.split('|');
                                    return (
                                        <option key={k} value={k}>
                                            {f} · {b.replace(/_/g, ' ')}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <p className="pnote">
                            {dayModel.fc} · {dayModel.brand.replace(/_/g, ' ')} · week {dayModel.selWeek} —{' '}
                            {fmt(dayModel.tp)} orders packed across {dayModel.dayArr.length} pack days,{' '}
                            {fmt(dayModel.ts)} with at least one short ({pct(dayModel.ts, dayModel.tp)}). Pack date is
                            the facility&apos;s local day and pack line comes from the batched scan, so these will not
                            tie exactly to a UTC daily cut — that is by design.
                        </p>
                        {/* eslint-disable-next-line react/no-danger */}
                        <div dangerouslySetInnerHTML={{ __html: dayBars(dayModel.dayArr) }} />
                    </div>

                    {/* ---- daily table (expandable) ---- */}
                    <div className="panel">
                        <p className="ptitle">Daily performance</p>
                        <p className="pnote">Click a row to see the items that drove that day.</p>
                        <table>
                            <thead>
                                <tr>
                                    <th>Pack date</th>
                                    <th>Packed</th>
                                    <th>Short</th>
                                    <th>Rate</th>
                                    <th>Per pkd</th>
                                    <th>Per aff</th>
                                    <th>0</th>
                                    <th>1</th>
                                    <th>2</th>
                                    <th>3+</th>
                                    <th>Swap</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dayModel.dayArr.map((e) => {
                                    const pp = e.packed ? e.lines / e.packed : 0;
                                    const pa = e.short ? e.lines / e.short : 0;
                                    const open = openDay === e.d;
                                    const items = open ? dayDetailItems(dayModel.brand, dayModel.fc, e.d, e.short) : [];
                                    return (
                                        <Fragment key={e.d}>
                                            <tr
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setOpenDay(open ? null : e.d)}
                                            >
                                                <td style={{ fontFamily: 'var(--mono)' }}>{e.d}</td>
                                                <td>{fmt(e.packed)}</td>
                                                <td>{fmt(e.short)}</td>
                                                <td
                                                    style={{
                                                        color:
                                                            e.packed && e.short / e.packed > 0.5 ? RAMP[7] : 'inherit',
                                                    }}
                                                >
                                                    {pct(e.short, e.packed)}
                                                </td>
                                                <td>{pp.toFixed(2)}</td>
                                                <td style={{ color: 'var(--muted)' }}>{pa ? pa.toFixed(2) : '—'}</td>
                                                <td style={{ color: RAMP[0] }}>{fmt(e.o0)}</td>
                                                <td>{fmt(e.o1)}</td>
                                                <td>{fmt(e.o2)}</td>
                                                <td style={{ fontWeight: 600 }}>{fmt(e.o3)}</td>
                                                <td style={{ color: e.swap ? RAMP[1] : 'var(--muted)' }}>
                                                    {e.swap ? fmt(e.swap) : '—'}
                                                </td>
                                            </tr>
                                            {open ? (
                                                <tr className="locrow">
                                                    <td colSpan={11}>
                                                        {items.length ? (
                                                            <>
                                                                <div className="sm" style={{ marginBottom: 6 }}>
                                                                    Items driving {e.d} — {fmt(e.short)} short orders
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        flexWrap: 'wrap',
                                                                        gap: '6px 14px',
                                                                    }}
                                                                >
                                                                    {items.map((h) => (
                                                                        <span
                                                                            key={h.sku}
                                                                            style={{
                                                                                fontSize: 12,
                                                                                whiteSpace: 'normal',
                                                                            }}
                                                                        >
                                                                            <b
                                                                                className="cat"
                                                                                style={{ color: h.catO.col }}
                                                                            >
                                                                                {h.catO.lab}
                                                                            </b>{' '}
                                                                            {h.name || h.sku}{' '}
                                                                            <em
                                                                                style={{
                                                                                    color: 'var(--muted)',
                                                                                    fontStyle: 'normal',
                                                                                }}
                                                                            >
                                                                                {fmt(h.n)} ·{' '}
                                                                                {(100 * h.share).toFixed(0)}%
                                                                            </em>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className="sm">
                                                                No item reached the 5-order reporting threshold on {e.d}
                                                                .
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* ---- item x day grid ---- */}
                    <div className="panel">
                        <p className="ptitle">Top items shorted, by day</p>
                        {dayModel.gridTop.length ? (
                            <>
                                <p className="pnote">
                                    {dayModel.fc} · {dayModel.brand.replace(/_/g, ' ')} — top {dayModel.gridTop.length}{' '}
                                    items across {dayModel.dayList.length} pack days. Cell is orders short and share of
                                    that day&apos;s short orders. Read across for a persistent shortage, down a column
                                    for a one-day spike.
                                </p>
                                <table>
                                    <thead>
                                        <tr>
                                            <th className="item">Item</th>
                                            {dayModel.dayList.map((d) => (
                                                <th key={d}>{d.slice(5)}</th>
                                            ))}
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dayModel.gridTop.map((it) => {
                                            const c = CATS[categorise(it.name)];
                                            return (
                                                <tr key={it.sku}>
                                                    <td className="item">
                                                        <span className="cat" style={{ color: c.col }}>
                                                            {c.lab}
                                                        </span>
                                                        {it.name || it.sku}
                                                    </td>
                                                    {dayModel.dayList.map((d) => {
                                                        const n = it.days[d] || 0;
                                                        const den = dayModel.dayShort[d] || 0;
                                                        const sh = den ? n / den : 0;
                                                        if (!n)
                                                            return (
                                                                <td
                                                                    key={d}
                                                                    style={{
                                                                        background: 'var(--tint)',
                                                                        color: '#C9D6DE',
                                                                    }}
                                                                >
                                                                    –
                                                                </td>
                                                            );
                                                        return (
                                                            <td
                                                                key={d}
                                                                style={{ background: gridShade(sh, dark) }}
                                                                title={`${d}: ${fmt(n)} orders, ${(100 * sh).toFixed(
                                                                    0,
                                                                )}% of that day's short orders`}
                                                            >
                                                                {fmt(n)}
                                                                <span className="subpct">{(100 * sh).toFixed(0)}%</span>
                                                            </td>
                                                        );
                                                    })}
                                                    <td style={{ fontWeight: 600 }}>{fmt(it.total)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </>
                        ) : (
                            <p className="pnote">
                                No item reached the 5-order reporting threshold for {dayModel.fc} on these pack days.
                            </p>
                        )}
                    </div>

                    {/* ---- by pack line ---- */}
                    <div className="panel">
                        <p className="ptitle">By pack line</p>
                        <p className="pnote">
                            {dayModel.lineArr.length > 1
                                ? `${dayModel.fc} · ${dayModel.brand.replace(
                                      /_/g,
                                      ' ',
                                  )} ran ${dayModel.lineArr.length} pack lines in week ${dayModel.selWeek}.`
                                : `${dayModel.fc} · ${dayModel.brand.replace(
                                      /_/g,
                                      ' ',
                                  )} ran a single pack line in week ${dayModel.selWeek}, so this matches the site total.`}
                        </p>
                        {dayModel.lineArr.length > 1 ? (
                            // eslint-disable-next-line react/no-danger
                            <div dangerouslySetInnerHTML={{ __html: lineBars(dayModel.lineArr) }} />
                        ) : null}
                        <table>
                            <thead>
                                <tr>
                                    <th>Pack line</th>
                                    <th>Days</th>
                                    <th>Packed</th>
                                    <th>Short</th>
                                    <th>Rate</th>
                                    <th>/packed</th>
                                    <th>3+ items</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dayModel.lineArr.map((e) => (
                                    <tr key={e.l}>
                                        <td style={{ fontFamily: 'var(--mono)' }}>{e.l}</td>
                                        <td>{e.days.size}</td>
                                        <td>{fmt(e.packed)}</td>
                                        <td>{fmt(e.short)}</td>
                                        <td>{pct(e.short, e.packed)}</td>
                                        <td style={{ fontWeight: 600 }}>
                                            {(e.packed ? e.lines / e.packed : 0).toFixed(2)}
                                        </td>
                                        <td>{pct(e.o3, e.packed)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}
            {!dayBusy && !dayErr && days && !dayModel ? (
                <div className="panel">
                    <p className="pnote">No day &amp; line detail for the loaded weeks.</p>
                </div>
            ) : null}
        </>
    );
}
